// src/pages/Performance/WorkflowAdmin.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Container,
  Card,
  CardHeader,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Typography,
  Stack,
  IconButton,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
  Autocomplete,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import api from "../../services/api";

/**
 * This admin page is made tolerant to different backend shapes:
 * - res.data can be [] or { data: [] } (Laravel Resource/Pagination)
 * - workflow.steps can be "steps" or "workflow_steps" etc.
 * - step fields can be step_name/title/name, actor_type/role_key/role, actor_resolver/resolver
 */

const ACTOR_TYPES = [
  { value: "self", label: "Employee (Self)" },
  { value: "line_manager", label: "Direct Manager" },
  { value: "second_level", label: "Second Level / HoD" },
  { value: "role", label: "Role (resolver)" },
  { value: "specific_user", label: "Specific User (resolver)" },
];

const COMMON_ROLE_KEYS = [
  "hr_admin",
  "admin_hr",
  "ceo",
  "operation_manager",
  "executive_manager",
  "head_of_department",
  "finance",
  "finance_coordinator",
  "project_manager",
  "manager",
  "supervisor",
];

const asArrayFromApi = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && payload.data && Array.isArray(payload.data.data)) return payload.data.data; // very defensive
  return [];
};

const isTempId = (id) =>
  typeof id === "string" && (id.startsWith("new-") || id.startsWith("tmp-"));

const isPersistedNumericId = (id) => {
  if (id === null || id === undefined) return false;
  if (typeof id === "number") return Number.isFinite(id);
  if (typeof id === "string" && id.trim() !== "" && !isTempId(id)) {
    const n = Number(id);
    return Number.isFinite(n) && String(n) === String(Number(id));
  }
  return false;
};

const toBool = (v, fallback = false) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["1", "true", "yes", "y"].includes(s)) return true;
    if (["0", "false", "no", "n"].includes(s)) return false;
  }
  return fallback;
};

const safeObj = (v) => {
  if (!v) return {};
  if (typeof v === "object") return v;
  // tolerate JSON string
  if (typeof v === "string") {
    try {
      const parsed = JSON.parse(v);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const pick = (...vals) => vals.find((v) => v !== undefined && v !== null);

const normalizeStepFromApi = (s, index) => {
  const actorResolver = safeObj(pick(s.actor_resolver, s.actorResolver, s.resolver));
  const actorTypeRaw = pick(s.actor_type, s.actorType, s.role_key, s.role, s.assignee_type);
  const actorType = (actorTypeRaw || "").toString().trim();

  const stepOrderRaw = pick(s.step_order, s.stepOrder, s.order, s.position);
  const stepOrder = Number.isFinite(Number(stepOrderRaw)) ? Number(stepOrderRaw) : index + 1;

  return {
    id: typeof s.id === "number" ? s.id : isPersistedNumericId(s.id) ? Number(s.id) : undefined,
    _localId: s._localId || (typeof s.id === "string" && isTempId(s.id) ? s.id : `tmp-${Date.now()}-${Math.random()}`),

    step_order: stepOrder,
    step_name: (pick(s.step_name, s.stepName, s.title, s.name, s.label) || "").toString(),

    actor_type: actorType || "self",
    actor_resolver: actorResolver,

    auto_skip_if_missing: toBool(pick(s.auto_skip_if_missing, s.autoSkipIfMissing), false),
    can_edit_scores: toBool(pick(s.can_edit_scores, s.canEditScores), true),
    can_edit_lower_scores: toBool(pick(s.can_edit_lower_scores, s.canEditLowerScores), false),
    can_edit_comments_only: toBool(pick(s.can_edit_comments_only, s.canEditCommentsOnly), false),
    is_required: toBool(pick(s.is_required, s.isRequired), true),
  };
};

const normalizeWorkflowFromApi = (wf) => {
  const stepsRaw = pick(wf.steps, wf.workflow_steps, wf.workflowSteps, wf.workflowStep, wf.workflow_steps_list);
  const steps = Array.isArray(stepsRaw)
    ? stepsRaw.map((s, i) => normalizeStepFromApi(s, i)).sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0))
    : [];

  return {
    id: wf.id,
    isNew: toBool(wf.isNew, false) || isTempId(wf.id),
    name: (wf.name || "").toString(),
    slug: (wf.slug || "").toString(),
    description: (wf.description || "").toString(),
    is_active: toBool(wf.is_active, true),

    allow_upstream_editing: toBool(wf.allow_upstream_editing, true),
    max_levels: Number.isFinite(Number(wf.max_levels)) ? Number(wf.max_levels) : 3,

    steps,
  };
};

function WorkflowAdmin() {
  const [tab, setTab] = useState(0);

  // ----- Workflows -----
  const [workflows, setWorkflows] = useState([]);
  const [wfLoading, setWfLoading] = useState(false);
  const [wfSavingId, setWfSavingId] = useState(null);
  const [wfSearch, setWfSearch] = useState("");
  const [wfError, setWfError] = useState(null);
  const [wfSuccess, setWfSuccess] = useState(null);

  // ----- Users / Assignments -----
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [assignSavingUserId, setAssignSavingUserId] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [userError, setUserError] = useState(null);
  const [userSuccess, setUserSuccess] = useState(null);

  const makeLocalStepId = () => `tmp-${Date.now()}-${Math.random()}`;

  const loadWorkflows = useCallback(async () => {
    try {
      setWfError(null);
      setWfSuccess(null);
      setWfLoading(true);

      const res = await api.get("/performance/workflows");
      const raw = asArrayFromApi(res?.data);
      const normalized = raw.map(normalizeWorkflowFromApi);

      // stable sort by name (then id)
      normalized.sort((a, b) => {
        const an = (a.name || "").toLowerCase();
        const bn = (b.name || "").toLowerCase();
        if (an < bn) return -1;
        if (an > bn) return 1;
        return String(a.id).localeCompare(String(b.id));
      });

      setWorkflows(normalized);
    } catch (e) {
      console.error("Failed to load workflows", e);
      setWfError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load workflows."
      );
      setWorkflows([]);
    } finally {
      setWfLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setUserError(null);
      setUserSuccess(null);
      setUsersLoading(true);

      const res = await api.get("/performance/workflow-users");
      const raw = asArrayFromApi(res?.data);

      // keep as-is but tolerant
      setUsers(
        raw.map((u) => ({
          ...u,
          department_name: u.department_name || u.department || "",
          project_name: u.project_name || u.project || "",
          actual_role: u.actual_role || u.role || "",
        }))
      );
    } catch (e) {
      console.error("Failed to load workflow users", e);
      setUserError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load users."
      );
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkflows();
    loadUsers();
  }, [loadWorkflows, loadUsers]);

  // -----------------------------
  // Workflows – create / edit / delete
  // -----------------------------
  const handleAddWorkflow = () => {
    setWfError(null);
    setWfSuccess(null);

    const tempId = `new-${Date.now()}`;
    setWorkflows((prev) => [
      {
        id: tempId,
        isNew: true,
        name: "",
        slug: "",
        description: "",
        is_active: true,
        allow_upstream_editing: true,
        max_levels: 3,
        steps: [],
      },
      ...prev,
    ]);
  };

  const handleWorkflowFieldChange = (id, field, value) => {
    setWorkflows((prev) =>
      prev.map((wf) => (wf.id === id ? { ...wf, [field]: value } : wf))
    );
  };

  const normalizeStepsForPayload = (wf) => {
    const steps = Array.isArray(wf.steps) ? [...wf.steps] : [];
    // sort by step_order (if user edited it)
    steps.sort((a, b) => Number(a.step_order ?? 0) - Number(b.step_order ?? 0));

    // enforce unique step_order in the outgoing payload
    const used = new Set();
    const normalized = steps.map((s, idx) => {
      let stepOrder = Number.isFinite(Number(s.step_order)) ? Number(s.step_order) : idx + 1;
      while (used.has(stepOrder)) stepOrder += 1;
      used.add(stepOrder);

      const actorType = (s.actor_type || "self").toString().trim() || "self";
      let resolver = safeObj(s.actor_resolver);

      // Clean resolver based on actor_type
      if (actorType !== "role" && actorType !== "specific_user") {
        resolver = {};
      } else if (actorType === "role") {
        // ensure resolver.role exists if user selected
        if (!resolver.role && s._role_key) resolver.role = s._role_key;
      } else if (actorType === "specific_user") {
        if (!resolver.user_id && s._user_id) resolver.user_id = s._user_id;
      }

      return {
        id: typeof s.id === "number" ? s.id : undefined,
        step_order: stepOrder,
        step_name: (s.step_name || `Step ${stepOrder}`).toString(),
        actor_type: actorType,
        actor_resolver: Object.keys(resolver || {}).length ? resolver : undefined,

        can_edit_scores: !!s.can_edit_scores,
        can_edit_lower_scores: !!s.can_edit_lower_scores,
        can_edit_comments_only: !!s.can_edit_comments_only,
        auto_skip_if_missing: !!s.auto_skip_if_missing,
        is_required: s.is_required === undefined ? true : !!s.is_required,
      };
    });

    return normalized;
  };

  const validateWorkflowBeforeSave = (wf) => {
    if (!wf.name || !wf.name.trim()) return "Please enter a workflow name.";

    const steps = Array.isArray(wf.steps) ? wf.steps : [];
    const stepOrders = steps
      .map((s) => Number(s.step_order))
      .filter((n) => Number.isFinite(n));

    const dupes = stepOrders.filter(
      (n, i) => stepOrders.indexOf(n) !== i
    );
    if (dupes.length) return "Duplicate Step # values found. Please make them unique.";

    // Validate role/specific_user resolvers where needed
    for (const s of steps) {
      const at = (s.actor_type || "").toString();
      if (at === "role") {
        const role = safeObj(s.actor_resolver).role || s._role_key;
        if (!role || !String(role).trim()) return "One of the steps uses Actor = Role but has no role key selected.";
      }
      if (at === "specific_user") {
        const uid = safeObj(s.actor_resolver).user_id || s._user_id;
        if (!uid || !Number.isFinite(Number(uid))) return "One of the steps uses Actor = Specific User but no user is selected.";
      }
    }

    return null;
  };

  const handleSaveWorkflow = async (wf) => {
    try {
      setWfError(null);
      setWfSuccess(null);

      const err = validateWorkflowBeforeSave(wf);
      if (err) {
        setWfError(err);
        return;
      }

      setWfSavingId(wf.id);

      const payload = {
        name: wf.name,
        description: wf.description,
        is_active: !!wf.is_active,
        allow_upstream_editing: wf.allow_upstream_editing === undefined ? true : !!wf.allow_upstream_editing,
        max_levels: Number.isFinite(Number(wf.max_levels)) ? Number(wf.max_levels) : 3,
        steps: normalizeStepsForPayload(wf),
      };

      const isNew = wf.isNew || isTempId(wf.id) || !isPersistedNumericId(wf.id);

      if (isNew) {
        await api.post("/performance/workflows", payload);
      } else {
        await api.put(`/performance/workflows/${wf.id}`, payload);
      }

      setWfSuccess("Workflow saved successfully.");
      await loadWorkflows();
    } catch (e) {
      console.error("Failed to save workflow", e);
      setWfError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to save workflow."
      );
    } finally {
      setWfSavingId(null);
    }
  };

  const handleDeleteWorkflow = async (wf) => {
    setWfError(null);
    setWfSuccess(null);

    const isNew = wf.isNew || isTempId(wf.id) || !isPersistedNumericId(wf.id);

    if (isNew) {
      setWorkflows((prev) => prev.filter((x) => x.id !== wf.id));
      return;
    }

    if (!window.confirm("Delete this workflow template?")) return;

    try {
      await api.delete(`/performance/workflows/${wf.id}`);
      setWfSuccess("Workflow deleted.");
      await loadWorkflows();
    } catch (e) {
      console.error("Failed to delete workflow", e);
      setWfError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to delete workflow."
      );
    }
  };

  const filteredWorkflows = useMemo(() => {
    const q = wfSearch.toLowerCase();
    if (!q) return workflows;
    return workflows.filter(
      (wf) =>
        wf.name?.toLowerCase().includes(q) ||
        wf.slug?.toLowerCase().includes(q) ||
        wf.description?.toLowerCase().includes(q)
    );
  }, [workflows, wfSearch]);

  const persistedWorkflowsForSelect = useMemo(() => {
    return workflows.filter((wf) => isPersistedNumericId(wf.id));
  }, [workflows]);

  // -----------------------------
  // Steps – add / edit / delete / reorder
  // -----------------------------
  const handleAddStep = (workflowId) => {
    setWfError(null);
    setWfSuccess(null);

    setWorkflows((prev) =>
      prev.map((wf) => {
        if (wf.id !== workflowId) return wf;

        const currentSteps = Array.isArray(wf.steps) ? [...wf.steps] : [];
        const nextOrder = currentSteps.length + 1;

        const newStep = {
          _localId: makeLocalStepId(),
          step_order: nextOrder,
          step_name: "",
          actor_type: "self",
          actor_resolver: {},
          can_edit_scores: true,
          can_edit_lower_scores: false,
          can_edit_comments_only: false,
          auto_skip_if_missing: false,
          is_required: true,
        };

        return { ...wf, steps: [...currentSteps, newStep] };
      })
    );
  };

  const handleStepFieldChange = (workflowId, stepKey, field, value) => {
    setWorkflows((prev) =>
      prev.map((wf) => {
        if (wf.id !== workflowId) return wf;

        const updatedSteps = (wf.steps || []).map((s) => {
          const key = s.id ?? s._localId;
          if (key !== stepKey) return s;

          // Special handling for actor_type changes: clear resolver if needed
          if (field === "actor_type") {
            const nextActor = String(value || "").trim() || "self";
            let nextResolver = safeObj(s.actor_resolver);

            if (nextActor !== "role" && nextActor !== "specific_user") {
              nextResolver = {};
              return { ...s, actor_type: nextActor, actor_resolver: nextResolver, _role_key: undefined, _user_id: undefined };
            }

            if (nextActor === "role") {
              // keep resolver.role if present
              return { ...s, actor_type: nextActor, actor_resolver: nextResolver, _user_id: undefined };
            }

            if (nextActor === "specific_user") {
              // keep resolver.user_id if present
              return { ...s, actor_type: nextActor, actor_resolver: nextResolver, _role_key: undefined };
            }
          }

          return { ...s, [field]: value };
        });

        return { ...wf, steps: updatedSteps };
      })
    );
  };

  const handleStepRoleChange = (workflowId, stepKey, roleKey) => {
    setWorkflows((prev) =>
      prev.map((wf) => {
        if (wf.id !== workflowId) return wf;
        const updatedSteps = (wf.steps || []).map((s) => {
          const key = s.id ?? s._localId;
          if (key !== stepKey) return s;

          const resolver = { ...safeObj(s.actor_resolver), role: roleKey };
          return { ...s, actor_resolver: resolver, _role_key: roleKey };
        });
        return { ...wf, steps: updatedSteps };
      })
    );
  };

  const handleStepUserChange = (workflowId, stepKey, user) => {
    const userId = user?.id ? Number(user.id) : null;
    setWorkflows((prev) =>
      prev.map((wf) => {
        if (wf.id !== workflowId) return wf;
        const updatedSteps = (wf.steps || []).map((s) => {
          const key = s.id ?? s._localId;
          if (key !== stepKey) return s;

          const resolver = { ...safeObj(s.actor_resolver), user_id: userId };
          return { ...s, actor_resolver: resolver, _user_id: userId };
        });
        return { ...wf, steps: updatedSteps };
      })
    );
  };

  const handleDeleteStep = (workflowId, stepKey) => {
    setWorkflows((prev) =>
      prev.map((wf) => {
        if (wf.id !== workflowId) return wf;

        const newSteps = (wf.steps || [])
          .filter((s) => (s.id ?? s._localId) !== stepKey)
          .sort((a, b) => Number(a.step_order ?? 0) - Number(b.step_order ?? 0))
          .map((s, index) => ({
            ...s,
            step_order: index + 1,
          }));

        return { ...wf, steps: newSteps };
      })
    );
  };

  const moveStep = (workflowId, stepKey, dir) => {
    setWorkflows((prev) =>
      prev.map((wf) => {
        if (wf.id !== workflowId) return wf;
        const steps = (wf.steps || [])
          .map((s) => ({ ...s }))
          .sort((a, b) => Number(a.step_order ?? 0) - Number(b.step_order ?? 0));

        const idx = steps.findIndex((s) => (s.id ?? s._localId) === stepKey);
        if (idx < 0) return wf;

        const swapWith = dir === "up" ? idx - 1 : idx + 1;
        if (swapWith < 0 || swapWith >= steps.length) return wf;

        const tmp = steps[idx].step_order;
        steps[idx].step_order = steps[swapWith].step_order;
        steps[swapWith].step_order = tmp;

        // Re-normalize to 1..n in order
        const normalized = steps
          .sort((a, b) => Number(a.step_order ?? 0) - Number(b.step_order ?? 0))
          .map((s, i) => ({ ...s, step_order: i + 1 }));

        return { ...wf, steps: normalized };
      })
    );
  };

  // -----------------------------
  // Users – assignment
  // -----------------------------
  const handleChangeUserTemplate = (userId, templateId) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId
          ? { ...u, workflow_template_id: templateId }
          : u
      )
    );
  };

  const handleSaveUserAssignment = async (user) => {
    try {
      setUserError(null);
      setUserSuccess(null);
      setAssignSavingUserId(user.id);

      await api.put(`/performance/workflow-users/${user.id}`, {
        workflow_template_id: user.workflow_template_id || null,
      });

      setUserSuccess("Assignment saved.");
      // optional refresh to reflect computed auto fields
      await loadUsers();
    } catch (e) {
      console.error("Failed to save user assignment", e);
      setUserError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to save user assignment."
      );
    } finally {
      setAssignSavingUserId(null);
    }
  };

  const distinctDepartments = useMemo(() => {
    const set = new Set();
    users.forEach((u) => {
      const d = (u.department_name || u.department || "").trim();
      if (d) set.add(d);
    });
    return Array.from(set).sort();
  }, [users]);

  const filteredUsers = useMemo(() => {
    let list = [...users];

    if (deptFilter) {
      list = list.filter((u) => (u.department_name || u.department || "") === deptFilter);
    }

    const q = userSearch.toLowerCase().trim();
    if (q) {
      list = list.filter((u) => {
        const name = (u.name || "").toLowerCase();
        const role = (u.actual_role || u.role || "").toLowerCase();
        const code = u.employee_code ? String(u.employee_code) : "";
        return name.includes(q) || role.includes(q) || code.includes(q);
      });
    }

    return list;
  }, [users, deptFilter, userSearch]);

  // -----------------------------
  // Render helpers
  // -----------------------------
  const renderWorkflowRow = (wf) => {
    const steps = Array.isArray(wf.steps)
      ? [...wf.steps].sort((a, b) => Number(a.step_order ?? 0) - Number(b.step_order ?? 0))
      : [];

    return (
      <Card
        key={wf.id}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          overflow: "hidden",
        }}
      >
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <TextField
                size="small"
                label="Name"
                value={wf.name || ""}
                onChange={(e) => handleWorkflowFieldChange(wf.id, "name", e.target.value)}
                sx={{ minWidth: 260 }}
              />

              <TextField
                size="small"
                label="Max Levels"
                type="number"
                value={wf.max_levels ?? 3}
                onChange={(e) => handleWorkflowFieldChange(wf.id, "max_levels", e.target.value)}
                inputProps={{ min: 1, style: { width: 110 } }}
              />

              <Chip
                label={wf.is_active ? "Active" : "Inactive"}
                color={wf.is_active ? "success" : "default"}
                size="small"
                onClick={() => handleWorkflowFieldChange(wf.id, "is_active", !wf.is_active)}
                sx={{ cursor: "pointer" }}
              />

              <Chip
                label={wf.allow_upstream_editing ? "Upstream edit: ON" : "Upstream edit: OFF"}
                color={wf.allow_upstream_editing ? "primary" : "default"}
                variant={wf.allow_upstream_editing ? "filled" : "outlined"}
                size="small"
                onClick={() =>
                  handleWorkflowFieldChange(wf.id, "allow_upstream_editing", !wf.allow_upstream_editing)
                }
                sx={{ cursor: "pointer" }}
              />
            </Stack>
          }
          subheader={
            <Typography variant="caption" color="text.secondary">
              slug: {wf.slug || "—"}
            </Typography>
          }
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton onClick={() => handleDeleteWorkflow(wf)} disabled={wfSavingId === wf.id}>
                <DeleteIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={() => handleSaveWorkflow(wf)} disabled={wfSavingId === wf.id}>
                {wfSavingId === wf.id ? <CircularProgress size={18} /> : <SaveIcon />}
              </IconButton>
            </Stack>
          }
        />

        <CardContent>
          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={wf.description || ""}
            onChange={(e) => handleWorkflowFieldChange(wf.id, "description", e.target.value)}
            sx={{ mb: 1.5 }}
          />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2">Steps</Typography>
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => handleAddStep(wf.id)}
            >
              Add step
            </Button>
          </Stack>

          {steps.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={70}>#</TableCell>
                  <TableCell>Step Name</TableCell>
                  <TableCell width={220}>Actor Type</TableCell>
                  <TableCell width={260}>Resolver</TableCell>
                  <TableCell>Flags</TableCell>
                  <TableCell width={120} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {steps.map((s, index) => {
                  const stepKey = s.id ?? s._localId ?? `row-${index}`;
                  const actorType = (s.actor_type || "self").toString();

                  const resolver = safeObj(s.actor_resolver);
                  const roleKey = resolver.role || s._role_key || "";
                  const userId = resolver.user_id || s._user_id || null;
                  const selectedUser =
                    actorType === "specific_user" && userId
                      ? users.find((u) => Number(u.id) === Number(userId)) || null
                      : null;

                  return (
                    <TableRow key={stepKey} hover>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={s.step_order ?? index + 1}
                          onChange={(e) =>
                            handleStepFieldChange(wf.id, stepKey, "step_order", e.target.value)
                          }
                          inputProps={{ min: 1, style: { width: 60 } }}
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={s.step_name || ""}
                          onChange={(e) =>
                            handleStepFieldChange(wf.id, stepKey, "step_name", e.target.value)
                          }
                          placeholder="e.g. Employee – Self Review"
                        />
                      </TableCell>

                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Actor</InputLabel>
                          <Select
                            label="Actor"
                            value={actorType}
                            onChange={(e) => handleStepFieldChange(wf.id, stepKey, "actor_type", e.target.value)}
                          >
                            {ACTOR_TYPES.map((a) => (
                              <MenuItem key={a.value} value={a.value}>
                                {a.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>

                      <TableCell>
                        {actorType === "role" ? (
                          <Stack spacing={1}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Role Key</InputLabel>
                              <Select
                                label="Role Key"
                                value={roleKey || ""}
                                onChange={(e) => handleStepRoleChange(wf.id, stepKey, e.target.value)}
                              >
                                <MenuItem value="">
                                  <em>Select role</em>
                                </MenuItem>
                                {COMMON_ROLE_KEYS.map((rk) => (
                                  <MenuItem key={rk} value={rk}>
                                    {rk}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <TextField
                              size="small"
                              label="Custom Role Key (optional)"
                              value={roleKey || ""}
                              onChange={(e) => handleStepRoleChange(wf.id, stepKey, e.target.value)}
                              placeholder="e.g. executive_manager"
                            />
                          </Stack>
                        ) : actorType === "specific_user" ? (
                          <Autocomplete
                            size="small"
                            value={selectedUser}
                            options={users}
                            getOptionLabel={(u) =>
                              `${u?.name || ""}${u?.employee_code ? ` (${u.employee_code})` : ""}`
                            }
                            isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
                            onChange={(_, v) => handleStepUserChange(wf.id, stepKey, v)}
                            renderInput={(params) => (
                              <TextField {...params} label="User" placeholder="Select user" />
                            )}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          <Chip
                            size="small"
                            label="Required"
                            variant={s.is_required ? "filled" : "outlined"}
                            color={s.is_required ? "primary" : "default"}
                            onClick={() =>
                              handleStepFieldChange(wf.id, stepKey, "is_required", !s.is_required)
                            }
                          />
                          <Chip
                            size="small"
                            label="Edit scores"
                            variant={s.can_edit_scores ? "filled" : "outlined"}
                            color={s.can_edit_scores ? "primary" : "default"}
                            onClick={() =>
                              handleStepFieldChange(wf.id, stepKey, "can_edit_scores", !s.can_edit_scores)
                            }
                          />
                          <Chip
                            size="small"
                            label="Lower scores"
                            variant={s.can_edit_lower_scores ? "filled" : "outlined"}
                            color={s.can_edit_lower_scores ? "primary" : "default"}
                            onClick={() =>
                              handleStepFieldChange(
                                wf.id,
                                stepKey,
                                "can_edit_lower_scores",
                                !s.can_edit_lower_scores
                              )
                            }
                          />
                          <Chip
                            size="small"
                            label="Comments only"
                            variant={s.can_edit_comments_only ? "filled" : "outlined"}
                            color={s.can_edit_comments_only ? "primary" : "default"}
                            onClick={() =>
                              handleStepFieldChange(
                                wf.id,
                                stepKey,
                                "can_edit_comments_only",
                                !s.can_edit_comments_only
                              )
                            }
                          />
                          <Chip
                            size="small"
                            label="Auto-skip"
                            variant={s.auto_skip_if_missing ? "filled" : "outlined"}
                            color={s.auto_skip_if_missing ? "primary" : "default"}
                            onClick={() =>
                              handleStepFieldChange(
                                wf.id,
                                stepKey,
                                "auto_skip_if_missing",
                                !s.auto_skip_if_missing
                              )
                            }
                          />
                        </Stack>
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveStep(wf.id, stepKey, "up")}
                                disabled={index === 0}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveStep(wf.id, stepKey, "down")}
                                disabled={index === steps.length - 1}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete step">
                            <IconButton size="small" onClick={() => handleDeleteStep(wf.id, stepKey)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No steps configured for this template yet.
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderUserRow = (u) => {
    const autoTemplateLabel =
      u.auto_template_name ||
      u.auto_workflow_name ||
      u.auto_template_slug ||
      u.auto_workflow_slug ||
      (u.auto_template && (u.auto_template.name || u.auto_template.slug)) ||
      "";

    return (
      <TableRow key={u.id} hover>
        <TableCell>{u.employee_code || "-"}</TableCell>
        <TableCell>{u.name || "-"}</TableCell>
        <TableCell>{u.department_name || "-"}</TableCell>
        <TableCell>{u.actual_role || "-"}</TableCell>
        <TableCell>{u.project_name || "-"}</TableCell>

        <TableCell>
          <Typography variant="caption" color="text.secondary">
            Auto: {autoTemplateLabel || "Default workflow"}
          </Typography>
        </TableCell>

        <TableCell sx={{ minWidth: 260 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Override Template</InputLabel>
            <Select
              label="Override Template"
              value={u.workflow_template_id || ""}
              onChange={(e) =>
                handleChangeUserTemplate(u.id, e.target.value === "" ? null : e.target.value)
              }
            >
              <MenuItem value="">
                <em>Use auto (by actual_role)</em>
              </MenuItem>
              {persistedWorkflowsForSelect.map((wf) => (
                <MenuItem key={wf.id} value={wf.id}>
                  {wf.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>

        <TableCell align="right">
          <Button
            variant="outlined"
            size="small"
            startIcon={
              assignSavingUserId === u.id ? (
                <CircularProgress size={14} />
              ) : (
                <SaveIcon fontSize="small" />
              )
            }
            disabled={assignSavingUserId === u.id}
            onClick={() => handleSaveUserAssignment(u)}
          >
            Save
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h5" sx={{ mb: 0.5 }}>
                Appraisal Workflows
              </Typography>
              <Typography variant="body2" color="text.secondary">
                HR Admin panel to manage workflow templates and assign overrides per employee.
              </Typography>
            </Box>

            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab label="Workflow Templates" />
              <Tab label="User Assignment" />
            </Tabs>
          </Stack>

          {(wfError || wfSuccess) && tab === 0 && (
            <Box sx={{ mt: 2 }}>
              {wfError && <Alert severity="error">{wfError}</Alert>}
              {wfSuccess && <Alert severity="success">{wfSuccess}</Alert>}
            </Box>
          )}

          {(userError || userSuccess) && tab === 1 && (
            <Box sx={{ mt: 2 }}>
              {userError && <Alert severity="error">{userError}</Alert>}
              {userSuccess && <Alert severity="success">{userSuccess}</Alert>}
            </Box>
          )}
        </CardContent>
      </Card>

      {tab === 0 && (
        <Card sx={{ borderRadius: 2 }}>
          <CardHeader
            title="Workflow Templates"
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={handleAddWorkflow}
                >
                  New workflow
                </Button>

                <TextField
                  size="small"
                  placeholder="Search by name / slug..."
                  value={wfSearch}
                  onChange={(e) => setWfSearch(e.target.value)}
                />

                <IconButton onClick={loadWorkflows}>
                  <RefreshIcon />
                </IconButton>
              </Stack>
            }
          />
          <Divider />

          <CardContent>
            {wfLoading ? (
              <Box
                sx={{
                  py: 6,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : filteredWorkflows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No workflows found.
              </Typography>
            ) : (
              filteredWorkflows.map(renderWorkflowRow)
            )}
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Card sx={{ borderRadius: 2 }}>
          <CardHeader
            title="Assign Workflows per Employee"
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Department</InputLabel>
                  <Select
                    label="Department"
                    value={deptFilter}
                    onChange={(e) => setDeptFilter(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {distinctDepartments.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  placeholder="Search name / code / role..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />

                <IconButton onClick={loadUsers}>
                  <RefreshIcon />
                </IconButton>
              </Stack>
            }
          />
          <Divider />

          <CardContent sx={{ p: 0 }}>
            {usersLoading ? (
              <Box
                sx={{
                  py: 6,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : filteredUsers.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No users found matching your filters.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Actual Role</TableCell>
                      <TableCell>Project</TableCell>
                      <TableCell>Auto Workflow</TableCell>
                      <TableCell>Override Template</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{filteredUsers.map(renderUserRow)}</TableBody>
                </Table>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  );
}

export default WorkflowAdmin;
